import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { apiGet, apiPost } from "../../services/genesisApi/httpsMethods";
import SponsorDetail from "./Sponsor";
import { ContactDetail } from "../contacts/Contact";
import type { Contact, Deal } from "../../types/deal";
import { renderSubTitle } from "../../components/renderComponents";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomButton } from "../../components/common/customButton";
import SponsorNotesCards from "./SponsorNotesCards";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { buildPipelineColumnDefs } from "../pipeline/pipelineColumnDefs";
import "../../assets/css/AGGrid-custom.css";

ModuleRegistry.registerModules([AllCommunityModule]);

interface SponsorWrapperProps {
  id?: number;
}

const SponsorWrapper: FC<SponsorWrapperProps> = ({ id }) => {
  const navigate = useNavigate();
  const params = useParams<{ sponsorId?: string }>();
  const sponsorId =
    id ?? (params.sponsorId ? parseInt(params.sponsorId) : undefined);
  const gridRef = useRef<AgGridReact>(null);
  const lists = useReferenceLists();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [sponsorRefreshKey] = useState(0);

  useEffect(() => {
    const fetchContacts = async () => {
      if (!sponsorId) return;

      try {
        const requestBody = {
          sponsor_id: sponsorId,
        };

        const response = await apiPost<Contact[]>(
          "contacts/search",
          requestBody,
        );
        if (response && response.success && response.data) {
          const data = response.data;
          setContacts(data);
        }
      } catch (error) {
        console.error("Failed to fetch contacts", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, [sponsorId]);

  useEffect(() => {
    const fetchDeals = async () => {
      if (!sponsorId) return;
      try {
        const response = await apiGet<Deal[]>(`deals?sponsor_id=${sponsorId}`);
        if (response && response.success && response.data) {
          setDeals(response.data);
        } else {
          setDeals([]);
        }
      } catch (error) {
        console.error("Failed to fetch deals for sponsor", error);
        setDeals([]);
      } finally {
        setDealsLoading(false);
      }
    };
    fetchDeals();
  }, [sponsorId]);

  const pipelineColumnDefs: ColDef[] = useMemo(
    () => buildPipelineColumnDefs({ editMode: false, lists }),
    [lists],
  );

  if (!sponsorId) {
    return <div>Sponsor ID not found.</div>;
  }

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box
        sx={{
          position: "sticky",
          zIndex: 10,
        }}
      >
        <SimpleToolbar>
          <CustomButton
            handleButtonPress={() => {
              navigate(`/sponsors/${sponsorId}/edit`);
            }}
            isButtonDisabled={false}
            text="Edit Sponsor"
            isLoading={false}
            isLoadingText="Loading"
            solid={false}
            widthOverride="100px"
            data-position="right"
          />
        </SimpleToolbar>
      </Box>
      <Box sx={{ padding: 2, marginBottom: 0, zIndex: 100 }}>
        <SponsorDetail
          sponsorId={sponsorId}
          isEdit={true}
          refreshTrigger={sponsorRefreshKey}
        />
        {renderSubTitle("Contacts")}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            marginTop: 1,
          }}
        >
          {contacts.length === 0 && !loading && (
            <Typography
              variant="subtitle1"
              sx={{ gridColumn: "span 3", marginBottom: 2 }}
            >
              No contacts found for this sponsor.
            </Typography>
          )}
          {contacts.map((contact) => (
            <ContactDetail
              key={contact.id}
              contact={contact}
              isEdit={false}
              isDealPage={true}
            />
          ))}
        </Box>

        {renderSubTitle("Deals")}
        <Box sx={{ width: "100%", height: 520, marginTop: 1 }}>
          {deals.length === 0 && !dealsLoading ? (
            <Typography variant="subtitle1" sx={{ marginBottom: 2 }}>
              No deals found for this sponsor.
            </Typography>
          ) : null}
          <div
            className="ag-theme-alpine"
            style={{ width: "100%", height: "100%" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={deals}
              columnDefs={pipelineColumnDefs}
              singleClickEdit={false}
              defaultColDef={{ flex: 0, resizable: true, filter: true }}
              rowHeight={30}
              getRowId={({ data }) => data?.id?.toString()}
            />
          </div>
        </Box>
        {renderSubTitle("Sponsor Notes")}
        <SponsorNotesCards sponsorId={sponsorId} />
      </Box>
    </Box>
  );
};

export default SponsorWrapper;
